import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Plus, Pencil, Loader2, Building2, X, LogIn } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

interface Client {
  id: string;
  name: string;
  allowed_domain_list: string[];
  template_list: string[];
  enabled: boolean;
  urlslug: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  urlslug: string;
  domainInput: string;
  allowed_domain_list: string[];
  template_list: string[];
}

interface TemplateOption {
  id: string;
  name: string;
}

interface ClientProfile {
  id: string;
  full_name: string | null;
  enabled: boolean;
}

const emptyForm: FormData = {
  name: "",
  urlslug: "",
  domainInput: "",
  allowed_domain_list: [],
  template_list: [],
};

const DATABULL_MCP = {
  name: "DataBull",
  url: "http://backend.databull.ai:8000/insyra/mcp/",
  internal: true,
  enabled: true,
} as const;

const Clients = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const [allTemplates, setAllTemplates] = useState<TemplateOption[]>([]);

  // Impersonation state
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateClient, setImpersonateClient] = useState<Client | null>(null);
  const [clientProfiles, setClientProfiles] = useState<ClientProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const fetchClients = async () => {
    const { data, error } = await supabase
      .from("client")
      .select("*")
      .eq("hide_from_ui", false)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setClients((data as Client[]) || []);
    }
    setLoading(false);
  };

  const fetchTemplates = async () => {
    const { data } = await supabase.from("template").select("id, name").order("name");
    setAllTemplates((data as TemplateOption[]) || []);
  };

  useEffect(() => {
    fetchClients();
    fetchTemplates();
  }, []);

  // --- Client CRUD ---
  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (client: Client, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditId(client.id);
    setForm({
      name: client.name,
      urlslug: client.urlslug || "",
      domainInput: "",
      allowed_domain_list: client.allowed_domain_list || [],
      template_list: (client.template_list as string[]) || [],
    });
    setDialogOpen(true);
  };

  const addDomain = () => {
    const domain = form.domainInput.trim().toLowerCase();
    if (!domain) return;
    if (form.allowed_domain_list.includes(domain)) {
      toast({ title: "Duplicate", description: "This domain is already in the list.", variant: "destructive" });
      return;
    }
    setForm((prev) => ({
      ...prev,
      domainInput: "",
      allowed_domain_list: [...prev.allowed_domain_list, domain],
    }));
  };

  const removeDomain = (domain: string) => {
    setForm((prev) => ({
      ...prev,
      allowed_domain_list: prev.allowed_domain_list.filter((d) => d !== domain),
    }));
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addDomain();
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Required", description: "Client name is required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      urlslug: form.urlslug.trim() || undefined,
      allowed_domain_list: form.allowed_domain_list,
      template_list: form.template_list,
    };
    let error;
    if (editId) {
      ({ error } = await supabase.from("client").update(payload).eq("id", editId));
    } else {
      const { data: newClient, error: insertError } = await supabase
        .from("client")
        .insert(payload)
        .select("id")
        .single();
      error = insertError;

      // After successful creation, provision the DataBull MCP data source
      if (!error && newClient && user) {
        try {
          // Temporarily switch superadmin's client_id to the new client so
          // that the restrictive RLS policy on data_sources allows the insert,
          // then revert to the original client_id.
          const { data: currentProfile } = await supabase
            .from("profiles")
            .select("client_id")
            .eq("id", user.id)
            .single();
          const originalClientId = currentProfile?.client_id;

          await supabase.from("profiles").update({ client_id: newClient.id }).eq("id", user.id);

          // Provision the DataBull MCP data source for the new client
          const { data: existing } = await supabase
            .from("data_sources")
            .select("id")
            .eq("client_id", newClient.id)
            .eq("url", DATABULL_MCP.url)
            .maybeSingle();
          if (!existing) {
            const { error: dsError } = await supabase.from("data_sources").insert({
              ...DATABULL_MCP,
              client_id: newClient.id,
              user_id: user.id,
            } as any);
            if (dsError) {
              console.warn("[Clients] failed to create DataBull data source:", dsError.message);
            }
          }

          // Revert superadmin's client_id back to their original client
          // if (originalClientId && originalClientId !== newClient.id) {
          //   await supabase.from("profiles").update({ client_id: originalClientId }).eq("id", user.id);
          // }

          // Trigger createclient on the MCP API (fire-and-forget)
          getSessionHeaders()
            .then((h) => supabase.functions.invoke("mcp-api/createclient", { headers: h }))
            .catch((err) => {
              console.warn("[createclient] edge function error:", err);
            });
        } catch (err) {
          console.warn("[Clients] error provisioning DataBull MCP:", err);
        }
      }
    }
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editId ? "Updated" : "Created", description: `Client "${form.name}" saved.` });
      setDialogOpen(false);
      fetchClients();
      if (!editId) {
        window.location.reload();
      }
    }
    setSaving(false);
  };

  // --- Impersonation ---
  const openImpersonate = async (client: Client, e: React.MouseEvent) => {
    e.stopPropagation();
    setImpersonateClient(client);
    setClientProfiles([]);
    setImpersonateDialogOpen(true);
    setProfilesLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, full_name, enabled")
      .eq("client_id", client.id)
      .order("full_name");
    setClientProfiles((data as ClientProfile[]) || []);
    setProfilesLoading(false);
  };

  const handleImpersonate = async (profileId: string) => {
    setImpersonating(profileId);
    try {
      const { data, error } = await supabase.functions.invoke("impersonate-user", {
        headers: await getSessionHeaders(),
        body: { target_user_id: profileId },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Save current session so we can return later
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      if (currentSession) {
        localStorage.setItem(
          "superadmin_return_session",
          JSON.stringify({
            access_token: currentSession.access_token,
            refresh_token: currentSession.refresh_token,
          }),
        );
      }

      // Sign in as the target user using the magic link OTP
      const { error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: "magiclink",
      });
      if (otpErr) throw otpErr;

      setImpersonateDialogOpen(false);
      toast({ title: "Impersonating", description: `Logged in as ${data.email}` });
      navigate("/");
    } catch (err: any) {
      toast({ title: "Impersonation failed", description: err.message, variant: "destructive" });
    }
    setImpersonating(null);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-muted-foreground">Manage client organizations and domains.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No clients yet</CardTitle>
          <CardDescription>Create your first client to get started.</CardDescription>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL Slug</TableHead>
                <TableHead>Allowed Domains</TableHead>
                <TableHead>Templates</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => {
                const templateNames = (client.template_list || [])
                  .map((tid) => allTemplates.find((t) => t.id === tid)?.name)
                  .filter(Boolean);
                return (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">{client.urlslug}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {client.allowed_domain_list.length > 0 ? (
                          client.allowed_domain_list.map((domain) => (
                            <Badge key={domain} variant="secondary" className="font-mono text-xs">
                              {domain}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">No domains</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {templateNames.length > 0 ? (
                          templateNames.map((name) => (
                            <Badge key={name} variant="outline" className="text-xs">
                              {name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={client.enabled}
                        onCheckedChange={async (checked) => {
                          // Prevent row click when toggling
                          const { error } = await supabase
                            .from("client")
                            .update({ enabled: checked })
                            .eq("id", client.id);
                          if (error) {
                            toast({ title: "Error", description: error.message, variant: "destructive" });
                            return;
                          }
                          setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, enabled: checked } : c)));
                          // When enabling a client, provision the Databull MCP server if not already present
                          if (checked && user) {
                            const { data: existing } = await supabase
                              .from("data_sources")
                              .select("id")
                              .eq("client_id", client.id)
                              .eq("url", DATABULL_MCP.url)
                              .maybeSingle();
                            if (!existing) {
                              await supabase.from("data_sources").insert({
                                ...DATABULL_MCP,
                                client_id: client.id,
                                user_id: user.id,
                              } as any);
                            }

                            // Trigger createclient on the MCP API (fire-and-forget)
                            getSessionHeaders()
                              .then((h) => supabase.functions.invoke("mcp-api/createclient", { headers: h }))
                              .catch((err) => {
                                console.warn("[createclient] edge function error:", err);
                              });
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(client.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => openImpersonate(client, e)}
                        title="Login as client user"
                      >
                        <LogIn className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => openEdit(client, e)} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Client" : "New Client"}</DialogTitle>
            <DialogDescription>
              {editId ? "Update client details." : "Create a new client organization."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="client-name">Name *</Label>
              <Input
                id="client-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Acme Corp"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="client-urlslug">URL Slug</Label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground whitespace-nowrap">*.databull.ai/</span>
                <Input
                  id="client-urlslug"
                  value={form.urlslug}
                  onChange={(e) => setForm({ ...form, urlslug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                  placeholder="auto-generated from name"
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground">Leave blank to auto-generate. Must be lowercase, alphanumeric and hyphens only.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="domain-input">Allowed Domains</Label>
              <div className="flex gap-2">
                <Input
                  id="domain-input"
                  value={form.domainInput}
                  onChange={(e) => setForm({ ...form, domainInput: e.target.value })}
                  onKeyDown={handleDomainKeyDown}
                  placeholder="e.g. acme.com"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addDomain}>
                  Add
                </Button>
              </div>
              {form.allowed_domain_list.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.allowed_domain_list.map((domain) => (
                    <Badge key={domain} variant="secondary" className="gap-1 font-mono text-xs pr-1">
                      {domain}
                      <button
                        onClick={() => removeDomain(domain)}
                        className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Templates</Label>
              {allTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No templates available.</p>
              ) : (
                <div className="flex flex-col gap-2 max-h-40 overflow-y-auto rounded-md border border-border p-2">
                  {allTemplates.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.template_list.includes(t.id)}
                        onCheckedChange={(checked) => {
                          setForm((prev) => ({
                            ...prev,
                            template_list: checked
                              ? [...prev.template_list, t.id]
                              : prev.template_list.filter((id) => id !== t.id),
                          }));
                        }}
                      />
                      {t.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editId ? "Save Changes" : "Create Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate User Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              Login as — {impersonateClient?.name}
            </DialogTitle>
            <DialogDescription>Select a user profile to impersonate.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 py-2 max-h-80 overflow-y-auto">
            {profilesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : clientProfiles.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No profiles found for this client.</p>
            ) : (
              clientProfiles.map((p) => (
                <Button
                  key={p.id}
                  variant="outline"
                  className="justify-between h-auto py-3"
                  disabled={impersonating === p.id}
                  onClick={() => handleImpersonate(p.id)}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">
                      {p.full_name || <span className="italic text-muted-foreground">Unnamed</span>}
                    </span>
                    <span className="text-xs text-muted-foreground">{p.enabled ? "Active" : "Disabled"}</span>
                  </div>
                  {impersonating === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                </Button>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
